import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { AuthShell } from "@/pages/Login";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Pick something you'll remember — at least 6 characters."
      footer={
        <>
          Changed your mind?{" "}
          <Link
            to="/login"
            className="font-semibold text-indigo-700 hover:underline"
          >
            Sign in
          </Link>
        </>
      }
    >
      {!token ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          This reset link is missing a token. Please request a new one.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pw">New password</Label>
            <Input
              id="pw"
              type="password"
              required
              data-testid="reset-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw2">Confirm password</Label>
            <Input
              id="pw2"
              type="password"
              required
              data-testid="reset-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
            />
          </div>
          <Button
            type="submit"
            data-testid="reset-submit-button"
            disabled={loading}
            className="group h-11 w-full gap-2 rounded-full bg-[#1A1D20] text-sm font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <KeyRound className="h-4 w-4" />
                Update password
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
